import { getBaseURL } from '../utils/config';
import { createUserInfo, TOWN_PORTAL_USER_ID } from '../components/UserContext';
import { mapTokenPermissionsToUser } from '../components/CaseReview/Offers/TownPortalFlow/townPortalLinkPermissions.js';
import { Magic } from 'magic-sdk';
import logger from '../utils/logger';
import {
  isLikelyUnreachableError,
  SERVER_UNREACHABLE,
  showUnreachableServerToastIfNeeded,
} from '../fetchConfig/requestErrors';

// Directly writing out id since its publishable and non secret.
const GOOGLE_API_CLIENT_ID = '511916914273-ip82hd3h57cjkjc00o6qoromqh89d32e.apps.googleusercontent.com';
const MAGIC_PUBLISHABLE_KEY = 'pk_live_7F24E87CF6B0BD43';

// Authentication types
export const AUTH_TYPES = {
  MAGIC: 'magic',
  GOOGLE: 'google',
  LINK: 'link'
};

// Error types for consistent error handling
export const AUTH_ERRORS = {
  UNAUTHORIZED_EMAIL: 'UNAUTHORIZED_EMAIL',
  INVALID_TOKEN: 'INVALID_TOKEN',
  SESSION_CREATION_FAILED: 'SESSION_CREATION_FAILED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  CONFIGURATION_ERROR: 'CONFIGURATION_ERROR',
  GENERIC_ERROR: 'GENERIC_ERROR'
};

/** Machine-readable codes returned by POST /auth/validate (`error_code` field). */
export const AUTH_ERROR_CODES = {
  LINK_INVITE_REVOKED: 'LINK_INVITE_REVOKED',
};

const LEGACY_LINK_INVITE_AUTH_ERROR_CODE = 'LINK_INVITE_REJECTED';

export const LINK_INVITE_AUTH_ERROR_MESSAGE =
  'The link used is no longer valid, please check your email for an updated link, or request access from your negotiating counterparty.';

export class AuthError extends Error {
  constructor(code, message = LINK_INVITE_AUTH_ERROR_MESSAGE) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

export function isLinkInviteRevokedError(error) {
  return (
    error?.code === AUTH_ERROR_CODES.LINK_INVITE_REVOKED ||
    error?.message === LEGACY_LINK_INVITE_AUTH_ERROR_CODE
  );
}

function isStoredLinkInviteRevokedCode(value) {
  return (
    value === AUTH_ERROR_CODES.LINK_INVITE_REVOKED ||
    value === LEGACY_LINK_INVITE_AUTH_ERROR_CODE
  );
}

const LINK_INVITE_AUTH_ERROR_STORAGE_KEY = 'link_invite_auth_error_code';

// Survives React 18 Strict Mode double-mount (first consume must not wipe storage early).
let consumedLinkInviteAuthErrorMemo = undefined;

/** Survives logout + full navigation (e.g. service worker session invalidation). */
export function persistLinkInviteAuthError() {
  try {
    sessionStorage.setItem(
      LINK_INVITE_AUTH_ERROR_STORAGE_KEY,
      AUTH_ERROR_CODES.LINK_INVITE_REVOKED
    );
    consumedLinkInviteAuthErrorMemo = undefined;
  } catch {
    // ignore
  }
}

export function consumePersistedLinkInviteAuthError() {
  if (consumedLinkInviteAuthErrorMemo !== undefined) {
    return consumedLinkInviteAuthErrorMemo;
  }
  try {
    const value = sessionStorage.getItem(LINK_INVITE_AUTH_ERROR_STORAGE_KEY);
    if (value) {
      sessionStorage.removeItem(LINK_INVITE_AUTH_ERROR_STORAGE_KEY);
      consumedLinkInviteAuthErrorMemo = isStoredLinkInviteRevokedCode(value)
        ? AUTH_ERROR_CODES.LINK_INVITE_REVOKED
        : null;
      return consumedLinkInviteAuthErrorMemo;
    }
  } catch {
    // ignore
  }
  consumedLinkInviteAuthErrorMemo = null;
  return null;
}

export function clearPersistedLinkInviteAuthError() {
  consumedLinkInviteAuthErrorMemo = undefined;
  try {
    sessionStorage.removeItem(LINK_INVITE_AUTH_ERROR_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Unified authentication service that handles all authentication flows
 */
class AuthService {
  constructor() {
    this.baseURL = getBaseURL();
  }

  getLinkSessionToken() {
    try {
      return sessionStorage.getItem('linkSessionToken');
    } catch {
      return null;
    }
  }

  setLinkSessionToken(token) {
    try {
      sessionStorage.setItem('linkSessionToken', token);
    } catch {}
  }

  clearLinkSessionToken() {
    try {
      sessionStorage.removeItem('linkSessionToken');
    } catch {}
  }

  getLinkUserInfo() {
    try {
      return sessionStorage.getItem('linkUserInfo');
    } catch {
      return null;
    }
  }

  setLinkUserInfo(userInfo) {
    try {
      sessionStorage.setItem('linkUserInfo', JSON.stringify(userInfo));
    } catch {}
  }

  clearLinkUserInfo() {
    try {
      sessionStorage.removeItem('linkUserInfo');
    } catch {}
  }

  async parseValidateErrorBody(response) {
    try {
      return await response.json();
    } catch {
      return { error: response.statusText };
    }
  }

  isLinkInviteRevokedResponse(status, errorData) {
    if (errorData?.error_code === AUTH_ERROR_CODES.LINK_INVITE_REVOKED) {
      return true;
    }
    return (
      status === 403 &&
      String(errorData?.error || '').includes('Link is no longer valid')
    );
  }

  throwIfLinkInviteRevoked(status, errorData) {
    if (this.isLinkInviteRevokedResponse(status, errorData)) {
      throw new AuthError(
        AUTH_ERROR_CODES.LINK_INVITE_REVOKED,
        errorData?.error || LINK_INVITE_AUTH_ERROR_MESSAGE
      );
    }
  }

  getActiveSessionToken() {
    return this.getLinkSessionToken() || localStorage.getItem('sessionToken');
  }

  decodeJwtPayload(token) {
    try {
      return JSON.parse(atob(String(token || '').split('.')[1]));
    } catch {
      return null;
    }
  }

  /**
   * Validate authentication with the backend
   * Checks if user is already authenticated, or validates new proof token
   * @param {string} authType - Type of authentication (magic, google, link)
   * @param {string} proofToken - Proof token for authentication
   * @returns {Promise<Object>} Session data with token and town view status
   */
  async validateAuth(authType, proofToken) {
    try {
      const sessionToken =
        authType === AUTH_TYPES.LINK ? null : localStorage.getItem('sessionToken');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      const response = await fetch(`${this.baseURL}/auth/validate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          auth_type: authType,
          proof_token: proofToken
        })
      });

      if (!response.ok) {
        const errorData = await this.parseValidateErrorBody(response);
        this.throwIfLinkInviteRevoked(response.status, errorData);
        throw new Error(`Authentication failed: ${errorData.error || response.statusText}`);
      }

      const sessionData = await response.json();
      logger.log('sessionData from auth validation', sessionData)

      // Store the session token (link auth is tab-scoped)
      if (authType === AUTH_TYPES.LINK) {
        this.setLinkSessionToken(sessionData.session_token);
      } else {
        localStorage.setItem('sessionToken', sessionData.session_token);
      }
      
      return {
        sessionToken: sessionData.session_token,
        isTownView: sessionData.is_town_view || false,
        alreadyAuthenticated: sessionData.already_authenticated || false
      };
    } catch (error) {
      logger.error('❌ Authentication validation failed:', error);
      throw error;
    }
  }

  /**
   * Handle Magic Link authentication
   * @param {string} email - User email
   * @returns {Promise<Object>} User info and session data
   */
  async authenticateWithMagic(email) {
    try {
      // Validate email format
      if (!email || !/\S+@\S+\.\S+/.test(email)) {
        throw new Error('Please enter a valid email address.');
      }

      // Check if user is authorized
      // if (!isUserAuthorized(email)) {
      //   throw new Error('This email is not authorized. Contact sales for access.');
      // }

      // Initialize Magic SDK
      const magic = new Magic(MAGIC_PUBLISHABLE_KEY);
      
      // Get DID token from Magic
      const didToken = await magic.auth.loginWithEmailOTP({ email });
      
      // Validate authentication
      const sessionData = await this.validateAuth(AUTH_TYPES.MAGIC, didToken);
      
      // Create user info
      const userInfo = createUserInfo(email, sessionData.isTownView);
      
      return {
        userInfo,
        sessionData,
        magic
      };
    } catch (error) {
      logger.error('Magic authentication error:', error);
      
      // If session creation failed due to unauthorized email, log out from Magic
      if (error.message && (error.message.includes('not authorized') || error.message.includes('Contact sales for access'))) {
        logger.log('🔐 AuthService: Logging out Magic user due to unauthorized email');
        try {
          // await magic.user.logout();
        } catch (logoutError) {
          logger.error('Error logging out Magic user:', logoutError);
        }
      }
      
      throw this.normalizeError(error);
    }
  }



  /**
   * Handle link-based authentication
   * @param {string} magicToken - Magic token from URL
   * @returns {Promise<Object>} User info and session data
   */
  async authenticateWithLink(magicToken) {
    try {
      try {
        // const payload = JSON.parse(atob(magicToken.split('.')[1]));
        // logger.log('🔗 Link auth: extracted magic_token payload', {
        //   sub: payload?.sub,
        //   muni_code: payload?.muni_code,
        //   tax_year: payload?.tax_year,
        //   purpose: payload?.purpose,
        //   link_auth: payload?.link_auth,
        //   share_workups: payload?.share_workups,
        //   show_one_pagers: payload?.show_one_pagers
        // });
      } catch (e) {
        logger.log('🔗 Link auth: unable to decode magic_token payload');
      }

      // Validate authentication
      const sessionData = await this.validateAuth(AUTH_TYPES.LINK, magicToken);
      
      const tokenPayload = this.decodeJwtPayload(sessionData.sessionToken) || {};

      // Create user object for link-authenticated users
      const linkUser = {
        email: tokenPayload.sub,
        userId: TOWN_PORTAL_USER_ID,
        userAccess: { level: 'link_auth', years: null },
        userName: 'Link Authenticated User',
        organization: 'Link Authenticated User',
        lastLoginTime: new Date().toISOString(),
        viewHistorical: false,
        isLinkAuthenticated: true,
        townView: sessionData.isTownView,
        muni_code: tokenPayload.muni_code,
        tax_year: tokenPayload.tax_year,
        court_date: tokenPayload.court_date || null,
        client_name: tokenPayload.client_name || 'Aventine',
        ...mapTokenPermissionsToUser(tokenPayload),
      };

      return {
        userInfo: linkUser,
        sessionData
      };
    } catch (error) {
      logger.error('Link authentication error:', error);
      throw error;
    }
  }

  /**
   * Handle automatic authentication on app initialization
   * @returns {Promise<Object|null>} User info if authenticated, null otherwise
   */
  async handleAutoAuthentication() {
    try {
      // Handle Google OAuth code directly on /signin to avoid a separate callback route
      const oauthParams = new URLSearchParams(window.location.search);
      const googleCode = oauthParams.get('code');
      const googleState = oauthParams.get('state');
      const magicToken = oauthParams.get('magic_token');

      if (!googleCode && !magicToken) {
        const linkInviteError = consumePersistedLinkInviteAuthError();
        if (linkInviteError) {
          throw new AuthError(linkInviteError);
        }
      }

      if (googleCode) {
        try {
          logger.log('🔐 AuthService: Processing Google OAuth code...');
          
          const redirectUri = window.location.origin + '/signin';

          // Exchange authorization code via backend (keeps client secret secure)
          const response = await fetch(`${this.baseURL}/auth/google-exchange`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              code: googleCode,
              redirect_uri: redirectUri
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Google authentication failed');
          }

          const authData = await response.json();
          
          // Store session token
          localStorage.setItem('sessionToken', authData.session_token);
          
          const sessionData = {
            sessionToken: authData.session_token,
            isTownView: authData.is_town_view || false
          };
          
          const completeUserInfo = createUserInfo(authData.email, sessionData.isTownView);

          // Store the target path for later redirect (after UserContext sets user state).
          // /snap sets authRedirectPath before leaving; don't replace that with /analysis.
          if (sessionStorage.getItem('authRedirectPath') !== '/snap') {
            if (googleState) {
              try {
                const originalParams = decodeURIComponent(googleState);
                const targetPath = originalParams.startsWith('?') ? `/${originalParams}` : `/?${originalParams}`;
                sessionStorage.setItem('authRedirectPath', targetPath);
              } catch {
                sessionStorage.setItem('authRedirectPath', '/analysis');
              }
            } else {
              sessionStorage.setItem('authRedirectPath', '/analysis');
            }
          }

          // Clean URL params but stay on /signin - let UserContext handle the redirect
          try {
            const newUrl = new URL(window.location.href);
            ['code','state','scope','authuser','prompt','hd'].forEach(k => newUrl.searchParams.delete(k));
            window.history.replaceState({}, '', newUrl.toString());
          } catch {}

          return { userInfo: completeUserInfo, sessionData };
        } catch (err) {
          const normalized = this.normalizeError(err);
          // Surface error to SignIn by placing it in the URL; UserContext will avoid overriding toast
          try {
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set('error', normalized.message);
            window.history.replaceState({}, '', newUrl.toString());
          } catch {}
          throw normalized;
        }
      }

      // Sign user in if they auth vs. email code
      if (magicToken) {
        // Link auth is tab-scoped; do not overwrite existing browser session
        this.clearLinkSessionToken();
        this.clearLinkUserInfo();
        const result = await this.authenticateWithLink(magicToken);
        
        // Clear the URL to prevent token reuse
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('magic_token');
        newUrl.searchParams.delete('share_workups');
        newUrl.searchParams.delete('show_one_pagers');
        newUrl.searchParams.delete('show_grid_comps');
        newUrl.pathname = `/${result.userInfo.client_name}`;
        window.history.replaceState({}, "", newUrl.toString());

        return result;
      }

      if (!googleCode && !magicToken && this.getLinkSessionToken()) {
        const linkSessionUserInfo = await this.validateCurrentSession();
        if (linkSessionUserInfo) {
          return linkSessionUserInfo;
        }
        throw new AuthError(AUTH_ERROR_CODES.LINK_INVITE_REVOKED);
      }

      // Prefer a live JWT (Google or prior Magic) so a stale Magic iframe session cannot
      // 401 session/refresh and abort auto-auth.
      const sessionUserInfo = await this.validateCurrentSession();
      if (sessionUserInfo) {
        return sessionUserInfo;
      }

      if (!googleCode) {
        try {
          const magic = new Magic(MAGIC_PUBLISHABLE_KEY);
          const isLoggedIn = await magic.user.isLoggedIn();
          if (isLoggedIn) {
            logger.log('MAGIC USER IS LOGGED IN, returning info and trying to create session');
            const userInfo = await magic.user.getInfo();
            const proofToken = await magic.user.getIdToken();
            const sessionData = await this.validateAuth(AUTH_TYPES.MAGIC, proofToken);
            return {
              userInfo: createUserInfo(userInfo.email, sessionData.isTownView),
              sessionData,
              magic
            };
          }
        } catch (error) {
          logger.warn('Magic auto-auth failed; continuing without Magic session', error);
          try {
            await new Magic(MAGIC_PUBLISHABLE_KEY).user.logout();
          } catch (logoutError) {
            logger.error('Error logging out Magic user:', logoutError);
          }
          const unauthorized = error.message && (error.message.includes('not authorized') || error.message.includes('Contact sales for access'));
          if (unauthorized || isLinkInviteRevokedError(error)) throw error;
        }
      }

      return null;
    } catch (error) {
      const normalized = this.normalizeError(error);
      logger.error('Auto authentication error:', error);
      throw normalized;
    }
  }

  /**
   * Normalize errors for consistent error handling
   * @param {Error} error - Original error
   * @returns {Error} Normalized error
   */
  normalizeError(error) {
    if (isLinkInviteRevokedError(error)) {
      return new AuthError(
        AUTH_ERROR_CODES.LINK_INVITE_REVOKED,
        error.message || LINK_INVITE_AUTH_ERROR_MESSAGE
      );
    }

    if (isLikelyUnreachableError(error)) {
      const err = new Error('Sign in failed.');
      err.code = SERVER_UNREACHABLE;
      return err;
    }

    const message = error.message || 'Authentication failed';
    
    if (message.includes('Unauthorized email domain') || message.includes('not authorized') || message.includes('Contact sales for access')) {
      return new Error('This email is not authorized. Contact sales for access.');
    }
    
    if (message.includes('Authentication failed:') || message.includes('Session creation failed')) {
      const errorMatch = message.match(/(?:Authentication|Session creation) failed: (.+)/);
      if (errorMatch) {
        const backendError = errorMatch[1];
        if (backendError.includes('aventine.ai')) {
          return new Error('Only @aventine.ai emails are authorized. Contact sales for access.');
        }
        if (backendError.includes('Invalid Google access token') || backendError.includes('Magic token validation failed')) {
          return new Error('Authentication failed. Please try again.');
        }
        if (backendError.includes('Google token not issued for this application')) {
          return new Error('Google authentication configuration error. Contact support.');
        }
        if (backendError.includes('magic-admin SDK not installed') || backendError.includes('MAGIC_SECRET not configured')) {
          return new Error('Authentication service unavailable. Contact support.');
        }
        if (backendError.includes('Token expired')) {
          return new Error('Your session has expired. Please sign in again.');
        }
      }
      return new Error('Authentication failed. Please try again.');
    }
    
    if (message.includes('Failed to send magic link')) {
      return new Error('Failed to send magic link. Please try again.');
    }
    
    return new Error('Sign in failed. Please try again.');
  }

  /** Show unreachable-server toast for auth failures; returns the error for inline UI. */
  presentAuthFailure(error) {
    showUnreachableServerToastIfNeeded(error, { context: 'sign in' });
    return error;
  }

  /**
   * Logout user and clear all authentication data
   */
  async logout() {
    try {
      // Clear localStorage + tab-scoped link auth
      this.clearLinkSessionToken();
      this.clearLinkUserInfo();
      localStorage.removeItem('userInfo');
      localStorage.removeItem('sessionToken');
      
      // Try to logout from Magic if available (non-blocking)
      try {
        const magic = new Magic(MAGIC_PUBLISHABLE_KEY);
        magic.user.logout().catch(error => {
          logger.error('Magic logout error:', error);
        });
      } catch (error) {
        logger.error('Error importing Magic SDK during logout:', error);
      }

      // Dispatch logout event to clear React state
      window.dispatchEvent(new CustomEvent('auth:logout'));
    } catch (error) {
      logger.error('Error during logout:', error);
    }
  }

  /**
   * Get Google OAuth URL for redirect
   * @returns {string} Google OAuth URL
   */
  getGoogleAuthUrl() {
    // Use /signin as redirect to handle OAuth code within the app
    const redirectUri = window.location.origin + '/signin';
    const scope = 'openid profile email';
    
    // Preserve current URL parameters by adding them to the state parameter
    const currentSearch = window.location.search;
    const state = currentSearch ? encodeURIComponent(currentSearch) : '';
    
    let authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_API_CLIENT_ID}&redirect_uri=${redirectUri}&response_type=code&scope=${encodeURIComponent(scope)}`;
    
    // Add state parameter if we have URL parameters to preserve
    if (state) {
      authUrl += `&state=${state}`;
    }
    
    return authUrl;
  }

  /**
   * Validate current JWT session and return user info if valid
   * @returns {Promise<Object|null>} User info if session is valid, null otherwise
   */
  async validateCurrentSession() {
    try {
      const sessionToken = this.getActiveSessionToken();

      if (!sessionToken) {
        logger.log('🔐 AuthService: No session token found, returning null');
        return null;
      }

      logger.log('🔐 AuthService: Validating existing session token...');
      
      // Use the unified validate endpoint - it will check the existing token
      const response = await fetch(`${this.baseURL}/auth/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      logger.log('🔐 AuthService: Validation response status:', response.status);

      if (response.status === 401) {
        // Token is invalid or expired
        logger.log('🔐 AuthService: Token invalid (401), clearing session data');
        if (this.getLinkSessionToken()) {
          this.clearLinkSessionToken();
          this.clearLinkUserInfo();
        } else {
          localStorage.removeItem('sessionToken');
          localStorage.removeItem('userInfo');
        }
        return null;
      }

      if (!response.ok) {
        const errorData = await this.parseValidateErrorBody(response);
        logger.log('🔐 AuthService: Validation failed with status:', response.status);
        this.throwIfLinkInviteRevoked(response.status, errorData);
        return null;
      }

      logger.log('🔐 AuthService: Token validation successful, retrieving stored user info');
      const storedUserInfo = this.getLinkSessionToken() ? this.getLinkUserInfo() : localStorage.getItem('userInfo');
      if (storedUserInfo) {
        try {
          const parsedInfo = JSON.parse(storedUserInfo);
          const tokenPayload = this.decodeJwtPayload(sessionToken);
          if (parsedInfo.isLinkAuthenticated) {
            parsedInfo.userId = TOWN_PORTAL_USER_ID;
            if (tokenPayload?.court_date) parsedInfo.court_date = tokenPayload.court_date;
            if (tokenPayload) Object.assign(parsedInfo, mapTokenPermissionsToUser(tokenPayload));
          }
          // logger.log('🔐 AuthService: Successfully retrieved user info:', parsedInfo);
          
          return {
            userInfo: parsedInfo,
            sessionData: {
              sessionToken: this.getActiveSessionToken(),
              isTownView: parsedInfo.townView || false
            }
          };
        } catch {
          logger.log('🔐 AuthService: Failed to parse stored user info, clearing data');
          if (this.getLinkSessionToken()) {
            this.clearLinkUserInfo();
          } else {
            localStorage.removeItem('userInfo');
          }
          return null;
        }
      }
      
      return null;
    } catch (error) {
      logger.error('❌ AuthService: Session validation error:', error);
      if (this.getLinkSessionToken()) {
        this.clearLinkSessionToken();
        this.clearLinkUserInfo();
      } else {
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('userInfo');
      }
      return null;
    }
  }
}

// Export singleton instance
export const authService = new AuthService(); 